const normalizeTitleText = (value = '') => String(value || '')
  .replace(/\s+/g, ' ')
  .trim();

const sentenceCase = (value = '') => {
  const text = normalizeTitleText(value);
  if (!text) {
    return '';
  }

  return `${text[0].toUpperCase()}${text.slice(1).toLowerCase()}`;
};

const titleCase = (value = '') => normalizeTitleText(value)
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const hasNegatedMissedDose = (text = '') => (
  /\b(?:no|not|never|without)\s+(?:missed?|skipped?)\s+(?:dose|doses|meds?|medications?)\b/i.test(text)
  || /\b(?:didn['’]?t|did not)\s+(?:miss|skip)\s+(?:a\s+)?(?:dose|meds?|medications?)\b/i.test(text)
);

const stripBehaviorContext = (value = '') => normalizeTitleText(value)
  .replace(/^[,;:\-\u2013\u2014\s]+/, '')
  .replace(/^(?:the|a|an|his|her|their|this|that|my|our|your)\s+/i, '')
  .replace(/\b(?:and|but|then|while|so)\b.*$/i, '')
  .trim();

const stripLeadingDateTime = (value = '') => normalizeTitleText(value)
  .replace(/^(?:early|mid|late)\s+\d{4}\b/i, '')
  .replace(/^(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+\d{4}\b/i, '')
  .replace(/^(?:(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+)?(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}(?:st|nd|rd|th)?(?:,)?(?:\s+(?:around\s+)?\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?))?/i, '')
  .replace(/^(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?(?=\s+[A-Z])/i, '')
  .replace(/^(?:around\s+)?\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?)\.?/i, '')
  .replace(/^[|,;:\-\u2013\u2014\s.]+/, '')
  .trim();

const isConciseBehaviorTitle = (value = '') => {
  const text = normalizeTitleText(value);
  if (!text) {
    return false;
  }

  return (
    text.length <= 32
    && !/[,.!?;:]/.test(text)
    && !/\b(?:and|but|then|while|so)\b/i.test(text)
    && !/^(?:he|she|they|the child|child|student|kid|a|an|the)\b/i.test(text)
  );
};

const buildConciseBehaviorTitle = (value = '') => {
  const normalized = stripLeadingDateTime(value);
  if (!normalized) {
    return 'Behavior note';
  }

  if (/\b(?:meltdown|tantrum|crisis)\b/i.test(normalized)) {
    return 'Meltdown';
  }

  if (isConciseBehaviorTitle(normalized)) {
    return sentenceCase(normalized);
  }

  const firstSentence = normalized.split(/(?<=[.!?])\s+/)[0] || normalized;
  const firstBehaviorClause = firstSentence.split(/\b(?:and|but|then|while|so)\b/i)[0].trim();

  if (isConciseBehaviorTitle(firstBehaviorClause)) {
    return sentenceCase(firstBehaviorClause);
  }

  if (/\b(?:rough|hard|tough)\s+(?:morning|day|afternoon|evening|time)\b/i.test(firstBehaviorClause)) {
    const match = firstBehaviorClause.match(/\b(?:rough|hard|tough)\s+(?:morning|day|afternoon|evening|time)\b/i)?.[0] || '';
    return sentenceCase(match);
  }

  if (/\b(?:transition|transitioning|covered(?:\s+(?:her|his|their))?\s+ears|headphones|sensory break|behavior-wise|there was one moment|calmed down|settled|noise|classroom|math|task)\b/i.test(firstBehaviorClause)) {
    return 'Transition frustration';
  }

  if (/\bbefore getting on (?:the )?school bus episode\b/i.test(firstBehaviorClause)) {
    return 'School bus episode';
  }

  if (/\bminor episode\b/i.test(firstBehaviorClause)) {
    return 'Minor episode';
  }

  const triedHitTargetMatch = firstBehaviorClause.match(/\b(?:tried|attempted)\s+(?:to\s+)?hit\s+(?<target>.+?)(?=\b(?:and|but|then|while|so)\b|[,.!?]|$)/i);
  if (triedHitTargetMatch?.groups?.target) {
    const target = stripBehaviorContext(triedHitTargetMatch.groups.target);
    return target ? `Tried to hit ${target}` : 'Tried to hit';
  }

  const personTriedHitMatch = firstBehaviorClause.match(/\b(?<person>[A-Z][a-z]+)\s+tried\s+hit\b/);
  if (personTriedHitMatch?.groups?.person) {
    return `${personTriedHitMatch.groups.person} tried to hit`;
  }

  const threwObjectMatch = firstBehaviorClause.match(/\bthrew\s+(?<object>.+?)(?=\b(?:and|but|then|while|so)\b|[,.!?]|$)/i);
  if (threwObjectMatch?.groups?.object) {
    return `Threw ${stripBehaviorContext(threwObjectMatch.groups.object)}`;
  }

  const stateContextMatch = firstBehaviorClause.match(
    /^(?:he|she|they|the child|child|student|kid)?\s*(?:got|was|became|seemed|appeared|looked|felt)\s+(?<state>upset|frustrat(?:ed|ing)|cranky|irritable|angry|anxious|sad|mad)\b(?:\s+(?<prep>during|while|when|because|after|before|at)\s+(?<context>.+?))?(?=\b(?:and|but|then|while|so)\b|[.!?]|$)/i
  );

  if (stateContextMatch?.groups?.state) {
    const state = sentenceCase(stateContextMatch.groups.state);
    const prep = String(stateContextMatch.groups.prep || 'during').trim().toLowerCase();
    const context = stripBehaviorContext(stateContextMatch.groups.context || '');
    if (context) {
      return `${state} ${prep} ${context}`;
    }
    return state;
  }

  const actionMatch = firstBehaviorClause.match(
    /\b(?<action>refused to participate|needed redirection|needed support|needed a break|trouble focusing|difficulty focusing|sat out|rejoined|returned to class|returned to group|cried|crying|threw (?:his|her|their)?\s*pencil|tried to hit|attempted to hit|hit|slapped?|kicked|pushed)\b/i
  );

  if (actionMatch?.groups?.action) {
    const action = sentenceCase(actionMatch.groups.action);
    if (/^Cried$/i.test(action) || /^Crying$/i.test(action)) {
      const whenMatch = firstBehaviorClause.match(/\b(?:cried|crying)\b(?:\s+when\s+(?<context>.+?))?(?=\b(?:and|but|then|while|so)\b|[.!?]|$)/i);
      const context = stripBehaviorContext(whenMatch?.groups?.context || '');
      return context ? `Cried when ${context}` : action;
    }

    if (/^Threw(?:\s+His|\s+Her|\s+Their)?\s+Pencil$/i.test(action)) {
      return 'Threw pencil';
    }

    if (/^Slapped?$/i.test(action)) {
      const targetMatch = firstBehaviorClause.match(/\bslapped?\s+(?<target>.+?)(?=\b(?:and|but|then|while|so)\b|[,.!?]|$)/i);
      const target = stripBehaviorContext(targetMatch?.groups?.target || '');
      return target ? `Slapped ${target}` : 'Slapped';
    }

    if (/^(?:Tried|Attempted) to hit$/i.test(action)) {
      const targetMatch = firstBehaviorClause.match(/\b(?:tried|attempted)\s+to\s+hit\s+(?<target>.+?)(?=\b(?:and|but|then|while|so)\b|[,.!?]|$)/i);
      const target = stripBehaviorContext(targetMatch?.groups?.target || '');
      return target ? `Tried to hit ${target}` : action;
    }

    return action;
  }

  if (firstBehaviorClause.length <= 32 && isConciseBehaviorTitle(firstBehaviorClause)) {
    return sentenceCase(firstBehaviorClause);
  }

  return 'Behavior note';
};

const buildConciseMilestoneTitle = (value = '') => {
  const text = normalizeTitleText(value);
  const lower = text.toLowerCase();

  if (!text) {
    return 'Milestone';
  }

  if (/\b(?:progress|improv(?:ed|ement)|success(?:ful)?|great job|did well|better than|nice improvement|accomplishment|achievement|proud|managed|handled|followed directions)\b/i.test(lower)) {
    return 'Progress';
  }

  if (/\bot\b/i.test(lower) && /\b(?:session|therapy|appointment|visit)\b/i.test(lower)) {
    return 'OT session';
  }

  if (/\b(?:speech|physical therapy|music therapy|therapy)\b/i.test(lower) && /\b(?:session|appointment|visit)\b/i.test(lower)) {
    return 'Therapy session';
  }

  if (/\b(?:session|appointment|visit)\b/i.test(lower)) {
    return 'Session';
  }

  return 'Milestone';
};

const buildMedicationTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Medication';

  const medNameMatch = source.match(/\b(?:depakote|lorazepam|ativan|abilify|guanfacine er|guanfacine|melatonin|clonidine|sertraline|adderall|vyvanse|concerta|ritalin|focalin|strattera|methylphenidate|dexedrine)\b/i);
  const medName = medNameMatch ? titleCase(medNameMatch[0]) : 'Medication';
  const doseMatch = source.match(/\b\d+(?:\.\d+)?\s?(?:mg|ml|mcg|g|units?)\b/i);

  if (/\bincreas(?:e|ed|ing)\b/i.test(source)) return `${medName} increased`;
  if (/\bdecreas(?:e|ed|ing)\b/i.test(source)) return `${medName} decreased`;
  if (/\bstopp(?:ed|ing)?\b/i.test(source)) return `${medName} stopped`;
  if (/\bstart(?:ed|ing)?\b/i.test(source)) return `${medName} started`;
  if (hasNegatedMissedDose(source)) {
    return /\b(?:gave|given|administered|took)\b/i.test(source) ? `${medName} given` : 'Medication status';
  }
  if (/\bmiss(?:ed)?\b/i.test(source)) return `${medName} missed`;
  if (doseMatch && medNameMatch) return `${medName} ${doseMatch[0]}`;
  if (/\b(?:gave|given|administered|took)\b/i.test(source)) return `${medName} given`;

  return medName;
};

const DAYTIME_NAP_PATTERN = /\b(?:nap|napped|napping|dozed?|dozing|catnap|rest time|rested|fell asleep(?: briefly)?|asleep)\b/i;
const DAYTIME_NAP_CONTEXT_PATTERN = /\b(?:after school|after lunch|before lunch|during the day|this afternoon|in the afternoon|at school|in class|on the bus|on the ride|in the car|on the couch|for a few minutes|briefly)\b/i;
const BEDTIME_SLEEP_PATTERN = /\b(?:bedtime|bed time|going to bed|went to bed|bed by|asleep by|settled to sleep|settled after music|fell asleep (?:quickly|fast|faster|tonight))\b/i;

const buildSleepTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Sleep';

  const wakeTimeMatch = source.match(/\bwakes?\s+up\s+(?:at\s+)?((?:1[0-2]|0?[1-9])(?::[0-5]\d)?\s*[AaPp][Mm])\b/i);
  if (wakeTimeMatch?.[1]) return `Woke up at ${wakeTimeMatch[1].toUpperCase().replace(/\s+/g, ' ')}`;
  if (/\bwakes?\s+up\b/i.test(source) || /\bwoke up\b/i.test(source) || /\bwoke\b/i.test(source)) return 'Woke up';
  if (/\brough night|slept poorly|poor sleep\b/i.test(source)) return 'Poor sleep';
  if (BEDTIME_SLEEP_PATTERN.test(source)) return 'Bedtime';
  if (/\bnap\b/i.test(source) || (DAYTIME_NAP_PATTERN.test(source) && DAYTIME_NAP_CONTEXT_PATTERN.test(source))) return 'Nap';
  if (/\bfell asleep\b/i.test(source)) return 'Fell asleep';
  return 'Sleep';
};

const buildFoodTitle = (text = '') => {
  const source = stripLeadingDateTime(text).toLowerCase();
  if (!source) return 'Food';
  const mealMatch = source.match(/\b(breakfast|lunch|dinner|snack|meal)\b/i);
  const meal = mealMatch ? sentenceCase(mealMatch[1]) : 'Food';

  if (/\bappetite\b/i.test(source)) return 'Appetite change';
  if (/\b(refused|did not eat|didn't eat|wouldn't eat|won't eat|not eat)\b/i.test(source)) return `${meal} refused`;
  if (/\bfew bites\b/i.test(source)) return `${meal} - few bites`;
  if (/\b(ate|eating|drank|drinking|had|had some)\b/i.test(source)) return meal === 'Food' ? 'Food eaten' : `${meal} eaten`;
  return meal;
};

const buildHealthTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Health';
  if (/\bappointment\b/i.test(source)) {
    if (/\bneurolog(?:y|ist)\b/i.test(source)) return 'Neurology appointment';
    if (/\bpsychiatr(?:y|ist)\b/i.test(source)) return 'Psychiatry appointment';
    if (/\bdoctor\b/i.test(source)) return 'Doctor appointment';
    return 'Appointment';
  }
  if (/\b(?:tired|fatigue|fatigued)\b/i.test(source) && /\bappetite\b/i.test(source)) return 'Fatigue and appetite change';
  if (/\bappetite\b/i.test(source)) return 'Appetite change';
  if (/\b(?:sleepy|groggy|sluggish|tired|fatigue|fatigued)\b/i.test(source)) return 'Fatigue';
  return 'Health';
};

const buildActivityTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Activity';
  if (/\bot\b/i.test(source)) return 'OT';
  if (/\b(?:energetic|jump(?:ed|ing)?|running|climbing|active)\b/i.test(source)) {
    return /\bafter school\b/i.test(source) ? 'After-school energy' : 'Active play';
  }
  if (/\bwalk\b/i.test(source)) return 'Walk';
  if (/\bpark\b/i.test(source)) return 'Park';
  if (/\bschool\b/i.test(source)) return 'School activity';
  if (/\brecess\b/i.test(source)) return 'Recess';
  if (/\boutside|outdoor\b/i.test(source)) return 'Outdoor time';
  if (/\bparticipat/i.test(source)) return 'Participation';
  return 'Activity';
};

const buildMoodTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Mood';
  const moodMatch = source.match(/\b(happy|affectionate|tired|anxious|worried|excited|calm|frustrated|upset|sad|content|nervous|proud|mixed|overstimulated|emotional)\b/i);
  if (moodMatch?.[1]) return titleCase(moodMatch[1]);
  if (/\blaughed\b/i.test(source)) return 'Happy';
  return 'Mood';
};

const buildLogTitle = (text = '') => {
  const source = stripLeadingDateTime(text);
  if (!source) return 'Note';
  if (/\bteam notes?\b|\bstaff notes?\b|\bpriority focus\b/i.test(source)) return 'Team Notes';
  if ((/\bvisit(?:ed|or)?\b/i.test(source) || /\bcame over\b|\bstopped by\b|\bspent time with\b/i.test(source))
    && /\b(?:family|grandma|grandmother|grandpa|grandfather|mom|mother|dad|father|parents?|aunt|uncle|cousin|brother|sister|siblings?)\b/i.test(source)) {
    return 'Family Visit';
  }
  if (/\bot\b/i.test(source) && /\b(?:session|therapy|therapist)\b/i.test(source)) {
    return 'OT session';
  }
  if (/\btherapy\b.*\brecommend|\btherapist\b.*\brecommend/i.test(source)) return 'Therapy recommendation';
  if (/\bbus\b.*\b(?:incident|upset|meltdown|behavior|happened)\b/i.test(source)) return 'Bus incident';
  if (/\bmorning routine\b|\bgetting ready\b|\bbefore school\b/i.test(source)) return 'Morning routine';
  if (/\bbedtime\b|\bgoing to bed\b/i.test(source)) return 'Bedtime';
  if (/\bmedicat(?:ion|ions|ed)\b|\bdose\b|\bprescription\b/i.test(source)) return 'Medication update';
  if (/\bhealth\b|\bdoctor\b|\bpediatrician\b|\bmedical\b|\bsymptom\b/i.test(source)) return 'Health update';
  if (/\bcommunicat|\brespond|\banswer|\bspeak|\bwords?\b/i.test(source)) return 'Communication';
  if (/\b(?:energetic|jump(?:ed|ing)?|running|climbing|active)\b/i.test(source)) {
    return buildActivityTitle(source);
  }
  if (/\b(?:dinner|breakfast|lunch|snack|crackers|ate|skipped|refused)\b/i.test(source)) {
    return buildFoodTitle(source);
  }
  if (/\b(?:happy|sad|anxious|excited|calm|frustrated|upset|tired|emotional)\b/i.test(source)) {
    return buildMoodTitle(source);
  }

  return 'Daily update';
};

const normalizeEntryTitleCategory = (category = '') => {
  const key = normalizeTitleText(category).toLowerCase().replace(/[\s-]+/g, '_');
  const aliases = {
    behavior_response: 'behavior',
    incident: 'behavior',
    meds: 'medication',
    medication_dose: 'medication',
    medication_admin: 'medication',
    medication_change: 'medication',
    food_intake: 'food',
    sleep_period: 'sleep',
    health_note: 'health',
    appointment: 'health',
    daily_log: 'log',
    daily_care: 'daily_care',
    general_note: 'log',
    routine: 'log',
    observation: 'log',
  };

  return aliases[key] || key || 'log';
};

const getTitleWordCount = (value = '') => normalizeTitleText(value).split(/\s+/).filter(Boolean).length;

const isSourceDateTitleHint = (title = '') => {
  const normalizedTitle = normalizeTitleText(title);
  const lowerTitle = normalizedTitle.toLowerCase();
  const monthPattern = '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
  const dateHeadingPattern = `(?:early|mid|late)\\s+\\d{4}|${monthPattern}\\.?(?:\\s+(?:\\d{4}|\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{4})?))?`;
  const dateHeadingRegex = new RegExp(`^(?:${dateHeadingPattern})$`, 'i');
  const importedDateTitleRegex = new RegExp(`^imported note for\\s+(?:${dateHeadingPattern})$`, 'i');

  return lowerTitle === 'source date'
    || lowerTitle === 'date'
    || lowerTitle === 'imported note'
    || importedDateTitleRegex.test(normalizedTitle)
    || dateHeadingRegex.test(normalizedTitle);
};

const isUsableCareEntryTitleHint = (title = '', category = '', sourceText = '') => {
  const normalizedTitle = normalizeTitleText(title);
  if (isGenericCareEntryTitle(normalizedTitle, category) || isSourceDateTitleHint(normalizedTitle)) {
    return false;
  }

  const titleWordCount = getTitleWordCount(normalizedTitle);
  const normalizedSource = normalizeTitleText(sourceText).toLowerCase();
  const lowerTitle = normalizedTitle.toLowerCase();
  if (category === 'medication' && /\bmiss(?:ed)?\b/i.test(lowerTitle) && hasNegatedMissedDose(sourceText)) {
    return false;
  }
  const looksCopiedFromBody = Boolean(
    normalizedSource
    && normalizedSource !== lowerTitle
    && normalizedSource.startsWith(lowerTitle)
  );

  return normalizedTitle.length <= 48
    && titleWordCount <= 7
    && !/[.!?]/.test(normalizedTitle)
    && !/[,;:]/.test(normalizedTitle)
    && !looksCopiedFromBody;
};

const buildCareEntryTitle = (category = 'log', text = '', { titleHint = '' } = {}) => {
  const normalizedCategory = normalizeEntryTitleCategory(category);
  const normalizedHint = normalizeTitleText(titleHint);
  if (isUsableCareEntryTitleHint(normalizedHint, normalizedCategory, text)) {
    return normalizedHint;
  }

  switch (normalizedCategory) {
    case 'behavior':
      return buildConciseBehaviorTitle(text) || 'Behavior incident';
    case 'medication':
      return buildMedicationTitle(text);
    case 'sleep':
      return buildSleepTitle(text);
    case 'food':
      return buildFoodTitle(text);
    case 'health':
      return buildHealthTitle(text);
    case 'activity':
      return buildActivityTitle(text);
    case 'mood':
      return buildMoodTitle(text);
    case 'milestone':
      return buildConciseMilestoneTitle(text) || 'Milestone';
    case 'bathroom':
    case 'toilet':
      return 'Bathroom';
    case 'sensory':
      return 'Sensory note';
    case 'daily_care':
      return 'Daily care';
    default:
      return buildLogTitle(text);
  }
};

const isGenericCareEntryTitle = (title = '', category = '') => {
  const normalizedTitle = normalizeTitleText(title).toLowerCase();
  const normalizedCategory = normalizeEntryTitleCategory(category);

  return !normalizedTitle
    || normalizedTitle === normalizedCategory
    || normalizedTitle === 'behavior'
    || normalizedTitle === 'medication'
    || normalizedTitle === 'meds'
    || normalizedTitle === 'food'
    || normalizedTitle === 'sleep'
    || normalizedTitle === 'activity'
    || normalizedTitle === 'mood'
    || normalizedTitle === 'toilet'
    || normalizedTitle === 'bathroom'
    || normalizedTitle === 'log'
    || normalizedTitle === 'note'
    || normalizedTitle === 'daily log'
    || normalizedTitle === 'important moment'
    || normalizedTitle === 'medication log'
    || normalizedTitle === 'behavior log'
    || normalizedTitle === 'activity log'
    || normalizedTitle === 'mood log';
};

export {
  buildCareEntryTitle,
  buildConciseBehaviorTitle,
  buildConciseMilestoneTitle,
  isConciseBehaviorTitle,
  isGenericCareEntryTitle,
  isUsableCareEntryTitleHint,
  normalizeEntryTitleCategory,
  stripBehaviorContext,
  stripLeadingDateTime,
};
